import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AreaStateAggregateType } from "../types/area-state-aggregate.type";
import { CommandTrackingView } from "@/types/command-tracking.type";
import { useAuth0 } from "@auth0/auth0-react";
import AreaSkeleton from "../../client-areas/skeleton";
import { Button } from "@/components/buttons";
import { useSendCommand } from "@/api/areas/mutations";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";
import { ModuleCommandStatusEnum } from "@/types/enums/command-status.enum";
//@ts-ignore unused import
import tw from "twin.macro";
import { RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

type AreaDataLoaderProps = {
  numberOfModulesInArea: number;
  children: React.ReactNode;
  areaId: string;
  areaName: string;
};

type AreaDataProviderType = {
  areaStateAggregate: AreaStateAggregateType;
  areaCommandTracking: CommandTrackingView;
  areaName: string;
  areaId: string;
  /**
   * Optimistic update à appeler juste avant `sendCommand` pour une action
   * de mouvement (UP/DOWN/OPEN/CLOSE/etc.). Cela écrit immédiatement un
   * faux payload `command-tracking` avec workflowStatus = SENDING et un
   * timestamp local, pour que les consommateurs (notamment le chrono)
   * n'affichent pas l'ancienne commande pendant le RTT vers le backend.
   * Le vrai payload SSE viendra écraser cet état dès qu'il arrivera.
   */
  applyOptimisticCommand: (command: ModuleCommandEnum) => void;
};

const AreaDataProvider = createContext<AreaDataProviderType>({
  areaStateAggregate: {} as AreaStateAggregateType,
  areaCommandTracking: {} as CommandTrackingView,
  areaName: "",
  areaId: "",
  applyOptimisticCommand: () => {},
});

const TIMEOUT_DELAY = 5 * 1000;

export const useAreaDataProvider = () => {
  const context = useContext(AreaDataProvider);
  if (!context) {
    throw new Error("useAreaDataProvider must be used within a AreaDataLoader");
  }
  return context;
};

const AreaDataLoader = ({
  numberOfModulesInArea,
  children,
  areaId,
  areaName,
}: AreaDataLoaderProps) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const [areaStateTrackingState, setAreaStateTrackingState] =
    useState<AreaStateAggregateType | null>(null);
  const [commandTrackingState, setCommandTrackingState] =
    useState<CommandTrackingView | null>(null);
  const { mutateAsync: sendCommand, isPending: isRefreshing } = useSendCommand({
    areaId,
  });
  const { t: translator } = useTranslation();

  const applyOptimisticCommand = useCallback(
    (command: ModuleCommandEnum) => {
      setCommandTrackingState((prev) => ({
        ...(prev ?? ({} as CommandTrackingView)),
        areaId,
        command,
        workflowStatus: ModuleCommandStatusEnum.SENDING,
        timestamp: new Date(),
        commandId: `optimistic-${Date.now()}`,
        passedActivities: prev?.passedActivities ?? [],
      }));
    },
    [areaId]
  );

  const [isSSEConnected, setIsSSEConnected] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [sseError, setSSEError] = useState(false);
  const timeoutRef = React.useRef<number | null>(null);
  const [isDataloadingTimeout, setIsDataloadingTimeout] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const isDataReadyRef = useRef(false);

  useEffect(() => {
    if (areaStateTrackingState && commandTrackingState) {
      isDataReadyRef.current = true;
      setIsDataReady(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [areaStateTrackingState, commandTrackingState]);

  useEffect(() => {
    let source: EventSource | null = null;

    getAccessTokenSilently().then((token) => {
      source = new EventSource(
        `${
          import.meta.env.VITE_API_URL
        }/module-interactions/subscribe-notifications/area/${areaId}?token=${token}`
      );

      source.onopen = () => {
        console.log(
          `Connecté aux notifciations SSE Bienesis API pour la parcelle ${areaId} ✅`
        );
        setSSEError(false);
        setIsSSEConnected(true);

        timeoutRef.current = window.setTimeout(() => {
          if (!isDataReadyRef.current) {
            setIsDataloadingTimeout(true);
            source?.close();
          }
        }, TIMEOUT_DELAY);
      };

      source.addEventListener("command-tracking", (e) => {
        const payload = JSON.parse(e.data) as CommandTrackingView;
        if (payload.areaId !== areaId) {
          console.info(
            `--- Command tracking for area ${payload.areaId} received, but expected ${areaId} ---`
          );
          return;
        }
        setCommandTrackingState(payload);
        console.log("Command tracking:", payload);
        setSSEError(false);
      });

      source.addEventListener("area-state-tracking", (e) => {
        const payload = JSON.parse(e.data) as AreaStateAggregateType;
        if (payload.areaId !== areaId) {
          console.info(
            `--- Area state tracking for area ${payload.areaId} received, but expected ${areaId}`
          );
          return;
        }
        setAreaStateTrackingState(payload);
        console.log("Area state tracking:", payload);
        setSSEError(false);
      });

      source.onerror = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setSSEError(true);
        setIsSSEConnected(false);
      };
    });

    return () => {
      if (source) {
        console.log(`Closing SSE for area ${areaId} 🔴`);
        source.close();
        setIsSSEConnected(false);
        setAreaStateTrackingState(null);
        setCommandTrackingState(null);
        setSSEError(false);
        setIsDataloadingTimeout(false);
        setIsDataReady(false);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [getAccessTokenSilently, areaId, refreshKey]);

  if (isDataloadingTimeout) {
    return (
      <div tw="flex flex-col gap-2 rounded-xl bg-white shadow-md border border-gray-200 max-w-[775px]">
        <div tw="flex flex-col gap-2 p-2">
          <p tw="text-2xl font-semibold">{areaName}</p>
          <p tw="text-xs font-normal text-gray-400 flex items-center gap-1">
            {numberOfModulesInArea} {translator("canopies")}{" "}
          </p>
          <div tw="flex items-center justify-between">
            <p tw="text-sm font-normal text-gray-400 flex items-center gap-1">
              {translator("noDataReceived")}
            </p>
            <Button
              repair
              tw="border-none shadow-md bg-gray-100"
              onClick={async () => {
                await sendCommand({
                  command: ModuleCommandEnum.GET_STATUS,
                });

                setIsDataloadingTimeout(false);
                setRefreshKey((prev) => prev + 1);
              }}
            >
              <RefreshCcw tw="w-5 h-5" css={isRefreshing && tw`animate-spin`} />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  //Reflechir a la problématique de l'init des data et de la "premiere commande"
  if (
    !isSSEConnected ||
    !isDataReady ||
    !areaStateTrackingState ||
    !commandTrackingState
  ) {
    return <AreaSkeleton />;
  }

  if (sseError && !isSSEConnected) {
    return (
      <div tw="flex flex-col items-center justify-center flex-wrap gap-10">
        <p tw="text-xl font-medium text-gray-900 text-center">
          Le chargement des données parcelle à échoué, veuillez contacter le
          support.
        </p>
        <Button
          onClick={() => {
            window.location.reload();
          }}
        >
          Recharger la page
        </Button>
      </div>
    );
  }

  if (areaStateTrackingState && commandTrackingState) {
    return (
      <AreaDataProvider.Provider
        value={{
          areaStateAggregate: areaStateTrackingState,
          areaCommandTracking: commandTrackingState,
          areaName,
          areaId,
          applyOptimisticCommand,
        }}
      >
        {children}
      </AreaDataProvider.Provider>
    );
  }
};

export default AreaDataLoader;
